import React, { useMemo, useState } from 'react';
import { App, Empty, Input } from 'antd';
import { Check, Search } from 'lucide-react';
import { Button } from '@/components/Button';
import type { AssessmentPackage } from '@/services/api';
import type { AdminUserPackage } from '@/types/user';
import { APP_CURRENCY } from '@/utils/constants/currency';
import { formatDate } from '@/utils/formatters';
import { UserActionSummary } from '@/components/UserActionSummary';
import styles from './ManagePackagesForm.module.scss';

interface ManagePackagesFormProps {
  userLabel: string;
  currentBalance: number;
  /** Null while loading or after a failed load — no cards, since every owned package would look deleted. */
  packages: AssessmentPackage[] | null;
  /** Null while loading — Grant stays disabled so an owned package can't be granted again unseen. */
  ownedPackages: AdminUserPackage[] | null;
  onGrant: (packageId: string) => Promise<void>;
  onRevoke: (packageId: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * One selectable card: a catalog package (`pkg` set), or a package the user
 * still holds active templates of that has since been deleted from the catalog
 * (`pkg` unset) — kept so it can still be revoked.
 */
interface PackageCard {
  id: string;
  name: string;
  pkg?: AssessmentPackage;
  owned?: AdminUserPackage;
}

/** How a revoke's credits add up: "100 each" or "100 + 150". Null for a single template. */
const formatCreditCalculation = (templateCredits: number[]): string | null => {
  if (templateCredits.length < 2) return null;
  const [first] = templateCredits;
  return templateCredits.every((c) => c === first)
    ? `${first.toLocaleString()} each`
    : templateCredits.map((c) => c.toLocaleString()).join(' + ');
};

/** "{user} will lose access…" for one template; otherwise says how many will go. */
const describeRemoval = (userLabel: string, templateCount: number): string => {
  if (templateCount === 1) return `${userLabel} will lose access to this package.`;
  if (templateCount === 2) return `${userLabel} has this package on their account twice. Both will be removed.`;
  return `${userLabel} has this package on their account ${templateCount} times. All ${templateCount} will be removed.`;
};

const CardStatus: React.FC<{ card: PackageCard }> = ({ card }) => {
  const { owned, pkg } = card;
  return (
    <>
      {!pkg && <span className={styles.unavailableBadge}>No longer available</span>}
      {owned && owned.activeTemplateCount > 0 && (
        <>
          {owned.openSittingCount > 0 && <span className={styles.inProgressBadge}>In progress</span>}
          <span className={styles.ownedBadge}>Owned ×{owned.activeTemplateCount}</span>
        </>
      )}
      {owned && owned.activeTemplateCount === 0 && owned.lastRevokedAt && (
        <span className={styles.revokedBadge}>Revoked {formatDate(owned.lastRevokedAt)}</span>
      )}
    </>
  );
};

interface PackagePickerProps {
  cards: PackageCard[];
  value: string | null;
  onChange: (packageId: string) => void;
}

const PackagePicker: React.FC<PackagePickerProps> = ({ cards, value, onChange }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => cards.filter((card) => card.name.toLowerCase().includes(search.trim().toLowerCase())),
    [cards, search],
  );

  return (
    <div className={styles.picker}>
      <Input
        placeholder="Search packages"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        prefix={<Search className={styles.searchIcon} />}
        className={styles.search}
      />

      {filtered.length === 0 ? (
        <Empty description="No packages found" className={styles.empty} />
      ) : (
        <div className={styles.cardGrid}>
          {filtered.map((card) => {
            const selected = value === card.id;
            return (
              <div
                key={card.id}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                className={[styles.card, selected ? styles.selected : ''].join(' ')}
                onClick={() => onChange(card.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(card.id);
                  }
                }}
              >
                <div className={styles.cardTitleRow}>
                  <div className={styles.cardTitle}>{card.name}</div>
                  <div className={styles.cardBadges}>
                    <CardStatus card={card} />
                    {selected && (
                      <Check className={styles.selectedIcon} />
                    )}
                  </div>
                </div>

                {card.pkg ? (
                  <>
                    <div className={styles.cardMetaValue}>
                      {card.pkg.questionSet.name} · {card.pkg.questionSet._count.items} questions
                    </div>

                    <div className={styles.cardStats}>
                      <span>
                        ${(card.pkg.unlockPriceCents / 100).toFixed(2)} {APP_CURRENCY}
                      </span>
                      <span className={styles.statsDot}>·</span>
                      <span>{card.pkg.unlockIncludedCredits} credits</span>
                      <span className={styles.statsDot}>·</span>
                      <span>{card.pkg.interviewCostCredits} credits/interview</span>
                    </div>
                  </>
                ) : (
                  <div className={styles.cardMetaValue}>Deleted from the catalog — can only be revoked</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ManagePackagesForm: React.FunctionComponent<ManagePackagesFormProps> = ({
  userLabel,
  currentBalance,
  packages,
  ownedPackages,
  onGrant,
  onRevoke,
  onCancel,
}) => {
  const { modal } = App.useApp();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState<'grant' | 'revoke' | null>(null);

  const cards = useMemo<PackageCard[]>(() => {
    if (!packages) return [];
    const owned = ownedPackages ?? [];
    const ownedById = new Map(owned.map((o) => [o.packageId, o]));
    const catalogIds = new Set(packages.map((pkg) => pkg.id));
    // A deleted package with only revoked templates gets no card — neither
    // action could apply to it.
    const orphans = owned
      .filter((o) => !catalogIds.has(o.packageId) && o.activeTemplateCount > 0)
      .map((o) => ({ id: o.packageId, name: o.name, owned: o }));
    return [
      ...packages.map((pkg) => ({ id: pkg.id, name: pkg.name, pkg, owned: ownedById.get(pkg.id) })),
      ...orphans,
    ];
  }, [packages, ownedPackages]);

  const selected = cards.find((card) => card.id === selectedId);
  const canGrant = Boolean(selected?.pkg) && ownedPackages !== null;
  const activeTemplateCount = selected?.owned?.activeTemplateCount ?? 0;

  // The caller toasts both outcomes. The selection is kept so the card's badge
  // updates in place.
  const run = async (action: 'grant' | 'revoke') => {
    if (!selected) return;
    setPending(action);
    try {
      await (action === 'grant' ? onGrant(selected.id) : onRevoke(selected.id));
    } catch {
      // Already reported by the caller.
    } finally {
      setPending(null);
    }
  };

  const confirmRevoke = () => {
    const owned = selected?.owned;
    if (!selected || !owned || activeTemplateCount === 0) return;
    const credits = owned.creditsToRevoke;
    const granted = owned.templateCredits.reduce((sum, c) => sum + c, 0);
    const calculation = formatCreditCalculation(owned.templateCredits);
    const capped = credits < granted;

    modal.confirm({
      title: `Remove access to ${selected.name}?`,
      icon: null,
      maskClosable: true,
      centered: true,
      // Wider than the confirm default to fit the credit breakdown.
      width: 480,
      content: (
        <div className={styles.confirmBody}>
          <p className={styles.confirmIntro}>{describeRemoval(userLabel, activeTemplateCount)}</p>

          <div className={styles.confirmSummary}>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Credits removed</span>
              <span className={styles.confirmValue}>
                {credits.toLocaleString()}
                {!capped && calculation && <span className={styles.confirmCalc}> ({calculation})</span>}
              </span>
            </div>
            {capped && (
              <div className={styles.confirmCalcNote}>
                The package added {granted.toLocaleString()} credits, but they only have{' '}
                {currentBalance.toLocaleString()} left. Credits already spent aren&apos;t taken back.
              </div>
            )}
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Balance after</span>
              <span className={styles.confirmValue}>
                {currentBalance.toLocaleString()} → {(currentBalance - credits).toLocaleString()}
              </span>
            </div>
          </div>

          <ul className={styles.confirmNotes}>
            {owned.openSittingCount > 0 && (
              <li>Their session in progress for this package will end and can&apos;t be resumed.</li>
            )}
            <li>Results from sessions they&apos;ve finished stay visible.</li>
            <li>This doesn&apos;t refund their payment. Issue any refund in Stripe.</li>
          </ul>
        </div>
      ),
      okText: 'Remove access',
      okButtonProps: { danger: true },
      onOk: () => run('revoke'),
    });
  };

  return (
    <div className={styles.wrapper}>
      <p className={styles.description}>
        Grant this user a package&apos;s assessment content and its included credits immediately,
        without going through Stripe checkout, or remove a package they already have.
      </p>

      <UserActionSummary label="Managing" userLabel={userLabel} currentBalance={currentBalance} />

      <PackagePicker cards={cards} value={selectedId} onChange={setSelectedId} />

      <div className={styles.actions}>
        <Button appearance="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          appearance="solid"
          danger
          disabled={activeTemplateCount === 0 || pending !== null}
          loading={pending === 'revoke'}
          onClick={confirmRevoke}
        >
          Remove access
        </Button>
        <Button
          appearance="solid"
          disabled={!canGrant || pending !== null}
          loading={pending === 'grant'}
          onClick={() => run('grant')}
        >
          Grant package
        </Button>
      </div>
    </div>
  );
};

export default ManagePackagesForm;
